import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Moon, Sun, MessageSquare, Globe, ChevronDown, Check, Search } from 'lucide-react';
import { SUPPORTED_LANGUAGES, getLanguageInfo, getLanguagesByRegion, loadLanguage } from '@/i18n';
import { GUEST_LANGUAGE_CHOICE_KEY } from './guestPrefs';
import { reloadManifest } from '@/lib/pwa-manifest';
import { getSupabase } from '@/lib/supabase';
import { Flag } from '@/components/ui/Flag';

interface GuestHeaderProps {
  propertyName?: string;
  unitName?: string;
  checkIn?: string;
  checkOut?: string;
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
  onChatClick: () => void;
  unreadMessages: number;
  logoUrl?: string | null;
  landlordName?: string | null;
  chatEnabled?: boolean;
  hostEmail?: string | null;
  /** Access token used to persist the language choice to bookings.guest_language. */
  accessToken?: string;
  /**
   * The host's own look at the portal (?preview=1): a language pick changes
   * only this screen — it must not rewrite the guest's language on the booking
   * (whole group) nor become this browser's saved guest choice.
   */
  isPreview?: boolean;
  /** An explicit language pick in the header (also in preview). */
  onLanguagePicked?: (code: string) => void;
}

function DefaultLogo() {
  return (
    <div className="flex items-center">
      <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto" />
    </div>
  );
}

// Modern Language Selector for Guest Header
function GuestLanguageSelector({
  darkMode,
  accessToken,
  isPreview = false,
  onLanguagePicked,
}: {
  darkMode: boolean;
  accessToken?: string;
  isPreview?: boolean;
  onLanguagePicked?: (code: string) => void;
}) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const currentLang = getLanguageInfo(i18n.language) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleLanguageChange = (code: string) => {
    // Load the bundle first so the UI doesn't flash English fallbacks, then
    // switch. Remember the explicit pick on this device — GuestPortalPage
    // honours it on the next visit (it used to force English for any language
    // that isn't "fully translated", reverting the guest's choice).
    void loadLanguage(code).then(() => i18n.changeLanguage(code));
    onLanguagePicked?.(code);
    if (!isPreview) {
      try {
        localStorage.setItem(GUEST_LANGUAGE_CHOICE_KEY, code);
      } catch {
        // Storage blocked — the choice still applies for this session.
      }
    }
    // Reload PWA manifest with new language
    reloadManifest();
    setIsOpen(false);
    setSearchQuery('');

    // Persist the choice on the booking so the next visit (and the landlord)
    // sees the guest's actual preferred language. Fire-and-forget — UX must
    // not wait on the network round-trip, and a failure shouldn't block the
    // user. If the RPC call fails (e.g. token expired) we just keep the
    // local change.
    if (accessToken && !isPreview) {
      void getSupabase()
        .rpc('set_guest_language', { p_access_token: accessToken, p_language: code })
        .then(({ error }) => {
          if (error && import.meta.env.DEV) {
            console.warn('Failed to persist guest language:', error.message);
          }
        });
    }
  };

  type Language = typeof SUPPORTED_LANGUAGES[number];

  const filterLanguages = (languages: readonly Language[]) => {
    if (!searchQuery) return [...languages];
    const query = searchQuery.toLowerCase();
    return languages.filter(
      lang =>
        lang.name.toLowerCase().includes(query) ||
        lang.nativeName.toLowerCase().includes(query) ||
        lang.code.toLowerCase().includes(query)
    );
  };

  const mainLanguages = filterLanguages(getLanguagesByRegion('main'));
  const europeLanguages = filterLanguages(getLanguagesByRegion('europe'));
  const worldLanguages = filterLanguages(getLanguagesByRegion('world'));
  const hasResults = mainLanguages.length > 0 || europeLanguages.length > 0 || worldLanguages.length > 0;

  const renderLanguageItem = (lang: Language) => {
    const isActive = i18n.language === lang.code;
    return (
      <button
        key={lang.code}
        onClick={() => handleLanguageChange(lang.code)}
        className={`flex items-center gap-3 w-full px-3 py-2.5 text-left transition-all duration-200 ${
          isActive
            ? darkMode
              ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border-l-2 border-cyan-400'
              : 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-600 border-l-2 border-cyan-500'
            : darkMode
              ? 'text-slate-300 hover:bg-slate-700/50'
              : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        <Flag languageCode={lang.code} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate text-sm">{lang.nativeName}</div>
          <div className={`text-xs truncate ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
            {lang.name}
          </div>
        </div>
        {isActive && (
          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}
      </button>
    );
  };

  const renderSection = (title: string, languages: Language[]) => {
    if (languages.length === 0) return null;
    return (
      <div>
        {title && (
          <div className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider ${
            darkMode ? 'text-slate-500 bg-slate-900/50' : 'text-gray-400 bg-gray-50'
          }`}>
            {title}
          </div>
        )}
        <div className="max-h-48 overflow-y-auto">
          {languages.map(renderLanguageItem)}
        </div>
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`group flex items-center gap-2 h-11 px-3 rounded-xl transition-all duration-300 ${
          darkMode
            ? 'bg-slate-800/60 hover:bg-slate-700/80 border border-slate-600/50 hover:border-slate-500/50 backdrop-blur-sm'
            : 'bg-white/60 hover:bg-white/80 border border-gray-200/50 hover:border-gray-300/50 backdrop-blur-sm shadow-sm hover:shadow'
        }`}
        aria-label={t('common.selectLanguage', 'Select language')}
        aria-expanded={isOpen}
      >
        <Globe className={`w-4 h-4 transition-colors ${
          darkMode ? 'text-blue-400' : 'text-blue-500'
        }`} />
        <span className={`text-sm font-semibold uppercase tracking-wide ${
          darkMode ? 'text-slate-200' : 'text-gray-700'
        }`}>
          {currentLang.code}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-all duration-300 ${
          isOpen ? 'rotate-180' : 'group-hover:translate-y-0.5'
        } ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
      </button>

      {isOpen && (
        // Phones: pinned under the header across the screen — anchored to the
        // button it ran off the left edge (the button isn't at the far right).
        // The header's backdrop-blur makes IT the containing block of this
        // "fixed" box, and in the installed PWA on notched iPhones the header
        // grows by the safe-area inset — so the offset must include it, or the
        // list covers the header buttons.
        <div className={`fixed inset-x-4 top-[calc(4.5rem+env(safe-area-inset-top))] z-50 sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-72 rounded-2xl border overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200 ${
          darkMode
            ? 'bg-slate-800/95 border-slate-700/50 shadow-2xl shadow-black/50 backdrop-blur-xl'
            : 'bg-white/95 border-gray-200/50 shadow-2xl shadow-gray-200/50 backdrop-blur-xl'
        }`}>
          {/* Search */}
          <div className={`p-3 border-b ${darkMode ? 'border-slate-700/50' : 'border-gray-100'}`}>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                darkMode ? 'text-slate-500' : 'text-gray-400'
              }`} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('guest.searchLanguage', 'Search language...')}
                className={`w-full h-10 pl-10 pr-3 rounded-xl text-sm transition-all duration-200 focus:outline-none focus:ring-2 ${
                  darkMode
                    ? 'bg-slate-900/50 text-white placeholder-slate-500 border-slate-700/50 focus:ring-blue-500/50 focus:border-blue-500/50'
                    : 'bg-gray-50/50 text-gray-900 placeholder-gray-400 border-gray-200/50 focus:ring-blue-400/50 focus:border-blue-400/50'
                } border`}
              />
            </div>
          </div>

          {/* Languages */}
          <div className="max-h-[350px] overflow-y-auto">
            {hasResults ? (
              <>
                {renderSection('', mainLanguages)}
                {renderSection('Europe', europeLanguages)}
                {renderSection('World', worldLanguages)}
              </>
            ) : (
              <div className={`px-4 py-8 text-center ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                <Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('guest.noLanguagesFound', 'No languages found')}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function GuestHeader({
  propertyName: _propertyName,
  unitName: _unitName,
  darkMode,
  setDarkMode,
  onChatClick,
  unreadMessages,
  logoUrl,
  landlordName,
  chatEnabled = true,
  hostEmail: _hostEmail,
  accessToken,
  isPreview = false,
  onLanguagePicked,
}: GuestHeaderProps) {
  // The chat button stays even when the host paused the chat: the guest can
  // still read (and see the unread badge for) what the host writes. The e-mail
  // and phone fallbacks live in the paused chat itself.
  void _hostEmail;
  void _propertyName; // Reserved for future header display
  void _unitName; // Reserved for future header display
  const { t } = useTranslation();

  return (
    <header
      className={`sticky top-0 z-50 safe-area-top transition-all duration-300 ${
        darkMode
          ? 'bg-slate-900/90 border-b border-slate-700/50'
          : 'bg-white/90 border-b border-gray-200/80'
      } backdrop-blur-xl`}
    >
      <div className="px-4 pt-4 pb-3 pr-5">
        <div className="flex items-center justify-between gap-4">
          {/* Logo only - unit name is shown in WelcomeCard */}
          <div className="flex-1 min-w-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={landlordName || 'Logo'}
                className="h-9 max-w-[140px] object-contain"
              />
            ) : (
              <DefaultLogo />
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Language Selector - Custom with flag */}
            <GuestLanguageSelector
              darkMode={darkMode}
              accessToken={accessToken}
              isPreview={isPreview}
              onLanguagePicked={onLanguagePicked}
            />

            {/* Dark Mode Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`group relative flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 overflow-hidden ${
                darkMode
                  ? 'bg-slate-800/60 border border-slate-600/50 hover:border-amber-500/50 backdrop-blur-sm'
                  : 'bg-white/60 border border-gray-200/50 hover:border-cyan-300/50 backdrop-blur-sm shadow-sm hover:shadow'
              }`}
              title={darkMode ? t('guest.lightMode', 'Light mode') : t('guest.darkMode', 'Dark mode')}
              aria-label={darkMode ? t('guest.lightMode', 'Light mode') : t('guest.darkMode', 'Dark mode')}
              aria-pressed={darkMode}
            >
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                darkMode
                  ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20'
                  : 'bg-gradient-to-br from-cyan-500/10 to-blue-500/10'
              }`} />
              {darkMode ? (
                <Sun className="w-5 h-5 text-amber-400 group-hover:text-amber-300 transition-colors relative z-10 group-hover:rotate-12 duration-300" />
              ) : (
                <Moon className="w-5 h-5 text-cyan-500 group-hover:text-cyan-600 transition-colors relative z-10 group-hover:-rotate-12 duration-300" />
              )}
            </button>

            {/* Chat Button + unread badge (also while the host paused the chat) */}
            <div className="relative">
              <button
                type="button"
                onClick={onChatClick}
                aria-label={[
                  t('guest.nav.chat', 'Chat'),
                  unreadMessages > 0 ? `(${unreadMessages})` : '',
                  !chatEnabled ? `— ${t('guest.chat.paused.title', 'Domaćin je pauzirao chat')}` : '',
                ].filter(Boolean).join(' ')}
                className={`group relative flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 overflow-hidden ${
                  darkMode
                    ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 hover:scale-105'
                    : 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-105'
                } ${chatEnabled ? '' : 'opacity-80'}`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <MessageSquare className="w-5 h-5 relative z-10 group-hover:scale-110 transition-transform duration-300" />
              </button>
              {/* Badge outside button to prevent clipping */}
              {unreadMessages > 0 && (
                <span className="absolute -top-1.5 -end-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-pink-600 px-1.5 text-[10px] font-bold text-white shadow-lg shadow-rose-500/40 ring-2 ring-white dark:ring-slate-900 pointer-events-none z-10">
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default GuestHeader;
